import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Drawer,
  IconButton,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import VisibilityIcon from "@mui/icons-material/Visibility";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import { toast } from "react-toastify";

import SettingsPanel from "../../components/SettingsPanel";
import handleDateTime from "../../../../Components/Common/HandleDateTime";
import {
  authFailureLogSummaryApiCall,
  authFailureLogDataApiCall,
} from "../../../../helpers/fakebackend_helper";

/**
 * Auth Failures (CRMit Change #100539).
 *
 * Every refused request across the platform lands in ZCRM_Master.AuthFailureLog,
 * one row per distinct failure per day; the API sums the days back up for the
 * chosen period. Sources:
 *
 *   AUTHFILTER - AuthFilter refused it (Access denied A1/A2/A3/A9, expired
 *                token E1001, Object not found, Developer access only)
 *   CAPABILITY - a controller refused it (missing capability, no licence,
 *                access block)
 *
 * Expired tokens (E1001) are normal traffic, so they are one counter per day
 * with no route, user or IP. The key or token itself is never stored; API and
 * token keys show as a short hash so a repeat offender can still be spotted.
 *
 * There is deliberately no resolve or archive here, unlike Server Errors: a
 * refusal is not a bug with a fix date.
 */

const TAB_FAILURES = "failures";
const TAB_IPS = "ips";
const TAB_USERS = "users";

const PERIOD_OPTIONS = [
  { value: 1, label: "Today" },
  { value: 7, label: "Last 7 days" },
  { value: 30, label: "Last 30 days" },
  { value: 90, label: "Last 90 days" },
];

const SOURCE_OPTIONS = ["ALL", "AUTHFILTER", "CAPABILITY"];
const AUTH_TYPE_OPTIONS = ["ALL", "JWT", "API_KEY", "API_KEY_URL", "TOKEN_KEY", "CRON", "NONE"];

const SOURCE_COLOUR = {
  AUTHFILTER: "warning",
  CAPABILITY: "info",
};

// The body status the refusal carried. 402 and 493-499 come from
// helper_genric___check_security_and_licence (sent as HTTP 401/402/403).
const CODE_LABEL = {
  401: "Not authenticated",
  402: "Missing capability",
  403: "Forbidden",
  409: "Refused",
  493: "User access blocked",
  494: "Company access blocked",
  496: "No user licence",
  498: "No company licence",
  499: "Trial ended, no licence",
};

const codeLabel = (code) =>
  CODE_LABEL[code] ? `${code} ${CODE_LABEL[code]}` : String(code ?? "-");

const Section = ({ title, children }) =>
  children ? (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
        {title}
      </Typography>
      <Box
        component="pre"
        sx={{
          m: 0,
          p: 1.5,
          borderRadius: 1,
          bgcolor: "action.hover",
          fontFamily: "monospace",
          fontSize: "0.78rem",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          maxHeight: 320,
          overflow: "auto",
        }}
      >
        {children}
      </Box>
    </Box>
  ) : null;

const whoLabel = (row) => {
  if (row?.UserName) return row.UserName;
  if (row?.User_Id) return `User #${row.User_Id}`;
  return "-";
};

const AuthFailureLog = () => {
  const [activeTab, setActiveTab] = useState(TAB_FAILURES);
  const [loading, setLoading] = useState(false);
  const [notInstalled, setNotInstalled] = useState("");

  const [groups, setGroups] = useState([]);
  const [ips, setIps] = useState([]);
  const [codes, setCodes] = useState([]);
  const [spikeHits, setSpikeHits] = useState(100);

  // Server-side filters - changing one reloads.
  const [days, setDays] = useState(7);
  const [source, setSource] = useState("ALL");
  const [authType, setAuthType] = useState("ALL");
  const [ipFilter, setIpFilter] = useState("");
  const [codeFilter, setCodeFilter] = useState("");
  // Client-side, over what came back.
  const [keyword, setKeyword] = useState("");

  const [pageSize, setPageSize] = useState(25);

  // Detail drawer
  const [detailRow, setDetailRow] = useState(null);
  const [detailDays, setDetailDays] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const filters = { Days: days };
      if (source !== "ALL") filters.Source = source;
      if (authType !== "ALL") filters.AuthType = authType;
      if (ipFilter) filters.IpAddress = ipFilter;
      if (codeFilter) filters.Code = codeFilter;

      const response = await authFailureLogSummaryApiCall([
        { authfailurelog_data: filters },
      ]);
      if (response?.status === 200) {
        setNotInstalled("");
        setGroups(response.authfailurelog_list || []);
        setIps(response.authfailurelog_ip_list || []);
        setCodes(response.authfailurelog_code_list || []);
        setSpikeHits(Number(response.SpikeHitsPerDay) || 100);
      } else {
        setNotInstalled(response?.message || "Could not load auth failures");
      }
    } catch (e) {
      toast.error("Could not load auth failures");
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days, source, authType, ipFilter, codeFilter]);

  const matchesKeyword = (values) => {
    const term = keyword.trim().toLowerCase();
    if (!term) return true;
    return values
      .filter(Boolean)
      .some((value) => String(value).toLowerCase().includes(term));
  };

  const groupRows = useMemo(
    () =>
      groups
        .filter((row) =>
          matchesKeyword([
            row.Message,
            row.Route,
            row.UrlPath,
            row.IpAddress,
            row.UserName,
            row.UserEmail,
            row.CompanyName,
            row.CredentialHash,
          ])
        )
        .map((row) => ({ ...row, id: row.GroupKey })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [groups, keyword]
  );

  const ipRows = useMemo(
    () =>
      ips
        .filter((row) => matchesKeyword([row.IpAddress]))
        .map((row) => ({ ...row, id: row.IpAddress })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ips, keyword]
  );

  // Who is being refused: the failures that carry a user, rolled up per user.
  const userRows = useMemo(() => {
    const byUser = {};
    groups.forEach((row) => {
      if (!row.User_Id) return;
      const key = `${row.User_Id}|${row.Company_Id || ""}`;
      if (!byUser[key]) {
        byUser[key] = {
          id: key,
          User_Id: row.User_Id,
          UserName: row.UserName,
          UserEmail: row.UserEmail,
          Company_Id: row.Company_Id,
          CompanyName: row.CompanyName,
          HitCount: 0,
          Failures: 0,
          Codes: new Set(),
          LastSeenDateTime: row.LastSeenDateTime,
        };
      }
      const entry = byUser[key];
      entry.HitCount += Number(row.HitCount) || 0;
      entry.Failures += 1;
      entry.Codes.add(codeLabel(row.Code));
      if (row.LastSeenDateTime > entry.LastSeenDateTime) {
        entry.LastSeenDateTime = row.LastSeenDateTime;
      }
    });
    return Object.values(byUser)
      .map((entry) => ({ ...entry, Codes: Array.from(entry.Codes).join(", ") }))
      .filter((row) =>
        matchesKeyword([row.UserName, row.UserEmail, row.CompanyName, row.Codes])
      )
      .sort((a, b) => b.HitCount - a.HitCount);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups, keyword]);

  const totalHits = codes.reduce((sum, row) => sum + (Number(row.HitCount) || 0), 0);
  const spikeCount = ips.filter((row) => Number(row.FlagSpike) === 1).length;

  // ------------------------------------------------------------------
  // Detail
  // ------------------------------------------------------------------

  const handleOpenDetail = async (row) => {
    setDetailRow(row);
    setDetailDays([]);
    setDetailLoading(true);
    try {
      const response = await authFailureLogDataApiCall(row.GroupKey);
      if (response?.status === 200) {
        setDetailDays(response.authfailurelog_list || []);
      } else {
        toast.error(response?.message || "Could not load the detail");
      }
    } catch (e) {
      toast.error("Could not load the detail");
    }
    setDetailLoading(false);
  };

  const showIp = (ip) => {
    setIpFilter(ip);
    setActiveTab(TAB_FAILURES);
  };

  // ------------------------------------------------------------------
  // Grids (MUI DataGrid v5 props)
  // ------------------------------------------------------------------

  const failureColumns = [
    {
      field: "LastSeenDateTime",
      headerName: "Last Seen",
      width: 165,
      renderCell: (params) => (params.value ? handleDateTime(params.value) : "-"),
    },
    {
      field: "Code",
      headerName: "Code",
      width: 190,
      renderCell: (params) => (
        <Chip
          size="small"
          label={codeLabel(params.value)}
          color={Number(params.value) >= 493 ? "error" : "default"}
          variant="outlined"
        />
      ),
    },
    {
      field: "Source",
      headerName: "Source",
      width: 120,
      renderCell: (params) => (
        <Chip
          size="small"
          label={params.value}
          color={SOURCE_COLOUR[params.value] || "default"}
          variant="outlined"
        />
      ),
    },
    {
      field: "Message",
      headerName: "Message",
      flex: 1,
      minWidth: 240,
      renderCell: (params) => (
        <Tooltip title={params.value || ""}>
          <Typography variant="body2" noWrap>
            {params.value || "-"}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: "UrlPath",
      headerName: "Where",
      width: 220,
      renderCell: (params) => (
        <Tooltip title={params.row.Route || ""}>
          <Typography variant="body2" noWrap sx={{ fontFamily: "monospace" }}>
            {params.value
              ? `${params.row.HttpMethod || ""} ${params.value}`.trim()
              : "-"}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: "UserName",
      headerName: "Who",
      width: 180,
      renderCell: (params) => (
        <Tooltip
          title={[params.row.UserEmail, params.row.CompanyName]
            .filter(Boolean)
            .join(" - ")}
        >
          <Typography variant="body2" noWrap>
            {whoLabel(params.row)}
            {params.row.CompanyName ? ` (${params.row.CompanyName})` : ""}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: "AuthType",
      headerName: "Auth",
      width: 110,
      renderCell: (params) => (
        <Tooltip
          title={
            params.row.CredentialHash
              ? `Key hash ${params.row.CredentialHash}`
              : ""
          }
        >
          <span>{params.value || "-"}</span>
        </Tooltip>
      ),
    },
    {
      field: "IpAddress",
      headerName: "IP",
      width: 140,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "HitCount",
      headerName: "Hits",
      width: 80,
      type: "number",
      renderCell: (params) => (
        <Tooltip
          title={`First seen ${
            params.row.FirstSeenDateTime
              ? handleDateTime(params.row.FirstSeenDateTime)
              : "-"
          } - on ${params.row.DaysSeen} day(s)`}
        >
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
    {
      field: "actions",
      headerName: "",
      width: 60,
      sortable: false,
      renderCell: (params) => (
        <Tooltip title="View each day and the latest request">
          <IconButton size="small" onClick={() => handleOpenDetail(params.row)}>
            <VisibilityIcon />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  const ipColumns = [
    {
      field: "IpAddress",
      headerName: "IP",
      width: 170,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <span>{params.value}</span>
          {Number(params.row.FlagSpike) === 1 && (
            <Tooltip title={`${spikeHits}+ refusals in a single day`}>
              <Chip size="small" color="error" label="Spike" />
            </Tooltip>
          )}
        </Box>
      ),
    },
    { field: "HitCount", headerName: "Hits", width: 90, type: "number" },
    {
      field: "PeakDayHits",
      headerName: "Busiest day",
      width: 110,
      type: "number",
    },
    { field: "RouteCount", headerName: "Routes", width: 90, type: "number" },
    { field: "UserCount", headerName: "Users", width: 80, type: "number" },
    { field: "CompanyCount", headerName: "Companies", width: 100, type: "number" },
    {
      field: "CredentialCount",
      headerName: "Keys",
      width: 80,
      type: "number",
      description: "Distinct API / token keys tried from this IP",
    },
    {
      field: "LastSeenDateTime",
      headerName: "Last Seen",
      width: 165,
      renderCell: (params) => (params.value ? handleDateTime(params.value) : "-"),
    },
    {
      field: "actions",
      headerName: "",
      width: 60,
      sortable: false,
      renderCell: (params) => (
        <Tooltip title="Show this IP's failures">
          <IconButton size="small" onClick={() => showIp(params.row.IpAddress)}>
            <FilterAltIcon />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  const userColumns = [
    {
      field: "UserName",
      headerName: "User",
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Tooltip title={params.row.UserEmail || ""}>
          <Typography variant="body2" noWrap>
            {whoLabel(params.row)}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: "CompanyName",
      headerName: "Company",
      width: 220,
      renderCell: (params) =>
        params.value ||
        (params.row.Company_Id ? `Company #${params.row.Company_Id}` : "-"),
    },
    { field: "Codes", headerName: "Refused with", flex: 1, minWidth: 220 },
    { field: "Failures", headerName: "Failures", width: 90, type: "number" },
    { field: "HitCount", headerName: "Hits", width: 80, type: "number" },
    {
      field: "LastSeenDateTime",
      headerName: "Last Seen",
      width: 165,
      renderCell: (params) => (params.value ? handleDateTime(params.value) : "-"),
    },
  ];

  const grid =
    activeTab === TAB_IPS
      ? { rows: ipRows, columns: ipColumns }
      : activeTab === TAB_USERS
      ? { rows: userRows, columns: userColumns }
      : { rows: groupRows, columns: failureColumns };

  const latest = detailDays[0];

  return (
    <SettingsPanel
      title="Auth Failures"
      description="Every refused request across the platform: users locked out by a capability, licence or access block, bad or stale keys, missing records answered as 401, and expired sessions (one daily counter). One line per distinct failure, summed over the period. Keys and tokens are never stored."
      tabs={[
        { value: TAB_FAILURES, label: "Failures", count: groupRows.length },
        { value: TAB_IPS, label: "By IP", count: ipRows.length },
        { value: TAB_USERS, label: "By User", count: userRows.length },
      ]}
      value={activeTab}
      onChange={setActiveTab}
    >
      <Box sx={{ p: 3, width: "100%", minWidth: 0, maxWidth: "100%" }}>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant="h5" component="h1" fontWeight="bold">
            Auth Failures
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            <TextField
              size="small"
              placeholder="Search message, route, user, IP..."
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              sx={{ minWidth: 240 }}
            />
            <TextField
              size="small"
              select
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              sx={{ minWidth: 150 }}
            >
              {PERIOD_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              select
              value={source}
              onChange={(e) => setSource(e.target.value)}
              sx={{ minWidth: 140 }}
            >
              {SOURCE_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {option === "ALL" ? "All sources" : option}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              select
              value={authType}
              onChange={(e) => setAuthType(e.target.value)}
              sx={{ minWidth: 140 }}
            >
              {AUTH_TYPE_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {option === "ALL" ? "All auth types" : option}
                </MenuItem>
              ))}
            </TextField>
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={load}>
              Refresh
            </Button>
          </Box>
        </Box>

        {notInstalled && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {notInstalled}
          </Alert>
        )}

        {/* Totals by code - click one to filter, again to clear. */}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2, alignItems: "center" }}>
          <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
            {totalHits.toLocaleString()} refusals
            {spikeCount > 0 ? ` - ${spikeCount} IP spike(s)` : ""}
          </Typography>
          {codes.map((row) => (
            <Chip
              key={row.Code}
              size="small"
              label={`${codeLabel(row.Code)}: ${Number(row.HitCount).toLocaleString()}`}
              color={String(codeFilter) === String(row.Code) ? "primary" : "default"}
              variant={String(codeFilter) === String(row.Code) ? "filled" : "outlined"}
              onClick={() =>
                setCodeFilter(String(codeFilter) === String(row.Code) ? "" : row.Code)
              }
            />
          ))}
          {ipFilter && (
            <Chip
              size="small"
              color="primary"
              label={`IP ${ipFilter}`}
              onDelete={() => setIpFilter("")}
            />
          )}
        </Box>

        <Paper elevation={3} sx={{ height: 600, width: "100%" }}>
          <DataGrid
            rows={grid.rows}
            columns={grid.columns}
            loading={loading}
            pageSize={pageSize}
            onPageSizeChange={setPageSize}
            rowsPerPageOptions={[10, 25, 50, 100]}
            disableSelectionOnClick
            onRowDoubleClick={(params) =>
              activeTab === TAB_FAILURES
                ? handleOpenDetail(params.row)
                : activeTab === TAB_IPS
                ? showIp(params.row.IpAddress)
                : null
            }
            sx={{ border: 0 }}
          />
        </Paper>

        {/* ---------------------------------------------------------- */}
        {/* Detail                                                     */}
        {/* ---------------------------------------------------------- */}
        <Drawer
          anchor="right"
          open={!!detailRow}
          onClose={() => setDetailRow(null)}
          PaperProps={{ sx: { width: { xs: "100%", md: 720 } } }}
        >
          <Box sx={{ p: 3 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: 2,
                mb: 2,
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="h6">
                  {codeLabel(detailRow?.Code)}{" "}
                  <Chip
                    size="small"
                    label={detailRow?.Source}
                    color={SOURCE_COLOUR[detailRow?.Source] || "default"}
                    variant="outlined"
                  />
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ wordBreak: "break-word" }}
                >
                  {detailRow?.Message}
                </Typography>
              </Box>
              <IconButton size="small" onClick={() => setDetailRow(null)}>
                <CloseIcon />
              </IconButton>
            </Box>

            {detailLoading && <CircularProgress size={22} />}

            {!detailLoading && latest && (
              <>
                <Section title="Summary">
                  {[
                    `HTTP ${latest.HttpStatus}  ${latest.HttpMethod || ""} ${latest.UrlPath || "(expired tokens are counted without a route)"}`,
                    latest.Route && `Route: ${latest.Route}`,
                    `Auth: ${latest.AuthType || "-"}${latest.CredentialHash ? `  (key hash ${latest.CredentialHash})` : ""}`,
                    `User: ${whoLabel(latest)}${latest.UserEmail ? ` <${latest.UserEmail}>` : ""}`,
                    `Company: ${latest.CompanyName || latest.Company_Id || "-"}`,
                    `IP: ${latest.IpAddress || "-"}`,
                    `Hits: ${detailRow?.HitCount} over ${detailRow?.DaysSeen} day(s) in the period`,
                  ]
                    .filter(Boolean)
                    .join("\n")}
                </Section>
                <Section title="Latest request (key and token values masked)">
                  {[
                    latest.SampleUrl && `URL: ${latest.SampleUrl}`,
                    latest.Host && `Host: ${latest.Host}`,
                    latest.Origin && `Origin: ${latest.Origin}`,
                    latest.UserAgent && `User agent: ${latest.UserAgent}`,
                  ]
                    .filter(Boolean)
                    .join("\n")}
                </Section>
                <Section title="By day (last 90 days)">
                  {detailDays
                    .map(
                      (day) =>
                        `${day.LogDate}   ${String(day.HitCount).padStart(6)} hit(s)   ${day.FirstSeenDateTime?.slice(11) || ""} - ${day.LastSeenDateTime?.slice(11) || ""}`
                    )
                    .join("\n")}
                </Section>
              </>
            )}
          </Box>
        </Drawer>
      </Box>
    </SettingsPanel>
  );
};

export default AuthFailureLog;
